import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PHUKET_AREA_PAGES, getAreaPage, type PhuketAreaPage } from "@/lib/phuket-area-pages";
import { SERVICES } from "@/lib/services";
import { PHUKET_SEGMENTS } from "@/lib/phuket-segments";
import { ProvincePageShell } from "@/components/ProvincePageShell";
import { PageGallery } from "@/components/PageGallery";
import { CheckCircle2, ChevronRight } from "lucide-react";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { getActiveProvince } from "@/provinces/_registry";
import { LongFormContent } from "@/components/LongFormContent";
import type { PageContent } from "@/content/site-pages";
import { getPhuketAreaCoords } from "@/lib/geo-coords";
import { COMPANY } from "@/lib/company";

export const Route = createFileRoute("/areas/$area")({
  loader: async ({ params }): Promise<{ area: PhuketAreaPage; longform?: PageContent }> => {
    const area = getAreaPage(params.area);
    if (!area) throw notFound();
    const { getAreaLongform } = await import("@/content/longform-areas");
    return { area, longform: getAreaLongform(area.slug) };
  },
  head: ({ loaderData }) => {
    const a = loaderData?.area;
    if (!a) return { meta: [{ title: "Area not found" }] };
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const lf = loaderData?.longform;
    const coords = getPhuketAreaCoords(a.slug);
    const base = pageHeadForProvince({
      title: a.metaTitle,
      description: a.metaDesc,
      path: `/areas/${a.slug}`,
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "พื้นที่บริการ", url: "/areas" },
              { name: a.nameTh, url: `/areas/${a.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Place",
            "@id": `${site}/areas/${a.slug}#place`,
            name: `${a.nameTh} (${a.nameEn})`,
            description: a.intro,
            address: {
              "@type": "PostalAddress",
              addressLocality: a.nameEn,
              addressRegion: a.districtTh,
              postalCode: a.postalCode,
              addressCountry: "TH",
            },
            ...(coords
              ? {
                  geo: {
                    "@type": "GeoCoordinates",
                    latitude: coords.lat,
                    longitude: coords.lng,
                  },
                }
              : {}),
            containedInPlace: { "@type": "AdministrativeArea", name: `${config.nameEn} Province` },
          }),
        },
        ...(coords
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "LocalBusiness",
                  "@id": `${site}/areas/${a.slug}#localbusiness`,
                  name: `Thai Notary Phuket — ${a.nameEn}`,
                  image: `${site}/og-image.jpg`,
                  url: `${site}/areas/${a.slug}`,
                  telephone: COMPANY.phone,
                  priceRange: "฿฿",
                  address: {
                    "@type": "PostalAddress",
                    addressLocality: a.nameEn,
                    addressRegion: a.districtTh,
                    postalCode: a.postalCode,
                    addressCountry: "TH",
                  },
                  geo: {
                    "@type": "GeoCoordinates",
                    latitude: coords.lat,
                    longitude: coords.lng,
                  },
                  areaServed: {
                    "@type": "Place",
                    name: `${a.nameTh} (${a.nameEn}), Phuket`,
                  },
                  serviceArea: {
                    "@type": "GeoCircle",
                    geoMidpoint: {
                      "@type": "GeoCoordinates",
                      latitude: coords.lat,
                      longitude: coords.lng,
                    },
                    geoRadius: 5000,
                  },
                }),
              },
            ]
          : []),
        ...(lf && lf.faqs.length > 0
          ? [{ type: "application/ld+json", children: JSON.stringify(buildFaqSchema(lf.faqs)) }]
          : []),
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/areas/${a.slug}`)) },
      ],
    };
  },
  component: AreaPage,
  notFoundComponent: () => (
    <div className="p-10 text-center"><h1 className="text-2xl font-bold">ไม่พบพื้นที่นี้</h1><Link to="/areas" className="text-gold">← กลับ</Link></div>
  ),
  errorComponent: ({ error }) => {
    if (typeof console !== "undefined") console.error("Area page error:", error);
    return (
      <div className="p-10 text-center">
        <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
        <p className="mt-2 text-sm text-muted-foreground">ไม่สามารถโหลดหน้าได้ กรุณาลองใหม่อีกครั้ง</p>
        <a href="/areas" className="mt-4 inline-block text-gold">← กลับหน้าพื้นที่</a>
      </div>
    );
  },
});

function AreaPage() {
  const { area, longform } = Route.useLoaderData();
  const services = SERVICES.filter((s) => area.topServices.includes(s.slug) || area.topServices.includes(s.id));
  const segments = PHUKET_SEGMENTS.filter((s) => area.topSegments.includes(s.slug));
  return (
    <ProvincePageShell
      eyebrow={`${area.districtTh} • ${area.postalCode}`}
      title={`Notary Public ${area.nameTh} (${area.nameEn})`}
      subtitle={area.intro}
      breadcrumb={[{ label: "Home", to: "/" }, { label: "พื้นที่บริการ", to: "/areas" }, { label: area.nameTh }]}
    >
      <div className="mx-auto max-w-7xl space-y-14 px-4 py-16">
        <section>
          <h2 className="text-2xl font-black text-primary">จุดเด่นในพื้นที่ {area.nameTh}</h2>
          <ul className="mt-4 grid gap-3 md:grid-cols-3">
            {area.highlights.map((h: string) => (
              <li key={h} className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {h}
              </li>
            ))}
          </ul>
          <div className="mt-4 text-sm text-muted-foreground">📍 Landmark: <strong>{area.landmark}</strong> • ค่าเดินทาง on-site: <strong>{area.travelFee}</strong></div>
        </section>

        <section>
          <h2 className="text-2xl font-black text-primary">บริการยอดนิยมใน {area.nameTh}</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => (
              <div key={s.id} className="rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-card)]">
                <h3 className="text-base font-bold text-primary">{s.title}</h3>
                <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-gold">{s.titleEn}</div>
                <p className="mt-2 text-xs text-muted-foreground">{s.desc}</p>
                {s.priceFrom && <div className="mt-3 text-sm font-bold text-gold">เริ่ม {s.priceFrom.toLocaleString()} บาท</div>}
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-black text-primary">กลุ่มลูกค้าหลักใน {area.nameTh}</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {segments.map((seg) => (
              <Link key={seg.slug} to="/for/$segment" params={{ segment: seg.slug }} className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-gold">
                <div className="text-3xl">{seg.emoji}</div>
                <h3 className="mt-2 text-sm font-bold text-primary group-hover:text-gold">{seg.titleTh}</h3>
                <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{seg.shortTh}</p>
                <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-gold">ดูบริการ <ChevronRight className="h-3 w-3" /></div>
              </Link>
            ))}
          </div>
        </section>

        <PageGallery
          category="general"
          seed={area.slug}
          locale="th"
          title={`ตัวอย่างงานจริงที่สำนักงานดำเนินการให้ลูกค้าย่าน ${area.nameTh}`}
        />

        <section>
          <h2 className="text-2xl font-black text-primary">พื้นที่ใกล้เคียง</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {PHUKET_AREA_PAGES.filter((a) => a.slug !== area.slug && a.district === area.district).slice(0, 8).map((a) => (
              <Link key={a.slug} to="/areas/$area" params={{ area: a.slug }} className="rounded-md border border-border bg-secondary/60 px-3 py-1.5 text-xs font-medium hover:border-gold hover:text-gold">{a.nameTh}</Link>
            ))}
          </div>
        </section>
      </div>
      {longform && <LongFormContent content={longform} />}
    </ProvincePageShell>
  );
}